import { Pbs as PbsWaveIcon } from '@ori-ui/icons';
import { Avatar, Stack, styled, Typography, useTheme } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { DetailedHTMLProps, FC, HTMLAttributes } from 'react';

import { useTranslations } from '../../hooks/useTranslations';
import { LiveStreamingStates, TooltipTestIds, TranslationKey, ReferralType } from '../../models';
import { formatUserName } from '../../utils';
import { FormattedMessage } from '../FormattedMessage';
import { LiveStreamingStateWrap } from '../common/LiveStreamingStateWrap';

/**
 * Avatar size. Value is in pixels
 */
const AVATAR_SIZE = 50;

/**
 * Max width of tooltip component.
 */
const ROOT_MAX_WIDTH = 230;

/**
 * How large fallback icon representing avatar should be
 */
const fallBackAvatarIconSize = 27;

/**
 * Tooltip Arrow width and height
 */
const ARROW_SIZE = 30; // px

/**
 * Tooltip arrow
 */
const ARROW_OFFSET = {
  x: 7, // px
  y: 0, // px
};

const Root = styled('div')(({ theme, onClick }) => ({
  maxWidth: ROOT_MAX_WIDTH,
  width: '100vw',
  position: 'relative',
  boxShadow: theme.shadows[4],
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  ...(onClick && { cursor: 'pointer' }),
}));

const ContentWrap = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: theme.spacing(3),
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  zIndex: 1,
  position: 'relative',
}));

const TitleWrap = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const Title = styled(Typography)({
  fontWeight: 'bold',
  lineHeight: '22px',
});

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.grey[900],
}));

const TooltipArrow = styled('div')(({ theme }) => ({
  overflow: 'hidden',
  position: 'absolute',
  zIndex: 0,
  width: ARROW_SIZE,
  height: ARROW_SIZE,
  top: ARROW_OFFSET.y,
  left: ARROW_OFFSET.x,
  boxShadow: theme.shadows[4],
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.common.white,
  transform: 'rotate(33deg) skewY(25deg)',
}));

export type TooltipBaseProps = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export type TooltipProps = TooltipBaseProps & {
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  photoUrl: string | null | undefined;
  liveStreamingState?: LiveStreamingStates;
  referralType: ReferralType;
};

export const getContentTranslationKey = (type: ReferralType): TranslationKey => {
  switch (type) {
    case ReferralType.PBS:
      return TranslationKey.ReferralMessageTooltip;
    case ReferralType.ECREF:
      return TranslationKey.InviteMessageTooltip;
    default:
      throw new Error('Unknown referral tooltip type.');
  }
};

export const Tooltip: FC<TooltipProps> = ({
  firstName,
  lastName,
  photoUrl,
  liveStreamingState,
  referralType,
  ...restProps
}) => {
  const { formatMessage } = useTranslations();
  const theme = useTheme();
  const { getTestIdProps } = useTestId();
  const fullName = formatUserName(firstName, lastName);

  return (
    <Root
      {...getTestIdProps(TooltipTestIds.ROOT)}
      {...restProps}
    >
      <TooltipArrow />
      <ContentWrap>
        <TitleWrap direction="row">
          <Title
            variant="subtitle1"
            color={theme.palette.grey[900]}
            {...getTestIdProps(TooltipTestIds.TITLE)}
          >
            {formatMessage(TranslationKey.Greetings)}
          </Title>
          <LiveStreamingStateWrap streamingActivity={liveStreamingState ?? LiveStreamingStates.UNKNOWN}>
            <StyledAvatar
              src={photoUrl ?? ''}
              alt={formatMessage(TranslationKey.SponsorPhoto)}
              {...getTestIdProps(TooltipTestIds.AVATAR)}
            >
              <PbsWaveIcon
                sx={{ width: fallBackAvatarIconSize, height: fallBackAvatarIconSize }}
                viewBox="2.8 4 16.5 16"
                {...getTestIdProps(TooltipTestIds.FALLBACK_AVATAR)}
              />
            </StyledAvatar>
          </LiveStreamingStateWrap>
        </TitleWrap>
        <Typography
          variant="body2"
          component="p"
          color={theme.palette.grey[900]}
          {...getTestIdProps(TooltipTestIds.TEXT)}
        >
          <FormattedMessage
            id={getContentTranslationKey(referralType)}
            values={{
              fullName,
              // eslint-disable-next-line react/no-unstable-nested-components -- ok
              emphasis: (chunks) => <strong {...getTestIdProps(TooltipTestIds.USER_NAME)}>{chunks}</strong>,
            }}
          />
        </Typography>
      </ContentWrap>
    </Root>
  );
};
